// components/CoursePlanner/PlanTimeline.tsx
/**
 * PlanTimeline (UI Tier 3) — the four-year arc at a glance. A horizontal
 * strip of semester nodes (season dot, term, credits, running total) above
 * the vertical grid, so load distribution across the whole plan is readable
 * without scrolling. Clicking a node scrolls the grid to that semester.
 *
 * UX-R1 task 3.2: the strip reads as a continuous progression, not tabs —
 * a spine connects the nodes and fills through the current term; terms
 * before the marker render slightly muted. The current-term marker
 * (v8.18.0 progress marker, `academic_plans.current_term`) is set directly
 * on the strip via each node's pin button (always rendered → touch works);
 * the old "Current term:" header dropdown is gone. Wall-clock fallback for
 * plans without a marker is unchanged (handled by the parent's
 * `currentSemester` derivation).
 *
 * Prior-credit pseudo-semesters (000008/000009) are omitted — they carry no
 * time information. Visibility is a persisted per-device preference.
 */

import { useState } from 'react';
import { ChevronDown, ChevronRight, MapPin } from 'lucide-react';
import {
  sortSemesterEntries,
  getSemesterDisplayName,
  isPriorCreditsSemester,
  toBackendSemesterCode,
} from '../../utils/semesterUtils';
import { calculateTotalCredits } from '../../utils/courseUtils';
import { getSeasonDotClass } from './SemesterGrid';
import type { EnrolledCourse } from '../../types';
import { useCreditUnit } from '../../contexts/InstitutionConfigContext';

const STORAGE_KEY = 'planner_timeline_visible_v1';

const readVisible = (): boolean => {
  try {
    return localStorage.getItem(STORAGE_KEY) !== 'false';
  } catch {
    return true;
  }
};

export interface PlanTimelineProps {
  semesters: Record<string, EnrolledCourse[]> | null | undefined;
  currentSemester: string;
  /** Set the plan's progress marker ("I'm currently in <term>"). When
   * provided, each non-current node shows a pin button that sets the
   * marker (UX-R1 3.2 — replaces the old header dropdown). */
  onCurrentTermChange?: (term: number) => void;
}

const PlanTimeline = ({ semesters, currentSemester, onCurrentTermChange }: PlanTimelineProps) => {
  const creditUnit = useCreditUnit();
  const [visible, setVisible] = useState(readVisible);

  const entries = sortSemesterEntries(Object.entries(semesters ?? {})).filter(
    ([semester]) => !isPriorCreditsSemester(semester)
  );

  if (entries.length < 2) return null;

  const toggle = (): void => {
    setVisible((prev) => {
      try {
        localStorage.setItem(STORAGE_KEY, String(!prev));
      } catch {
        // Private mode — preference just won't persist.
      }
      return !prev;
    });
  };

  const scrollToSemester = (semester: string): void => {
    document
      .querySelector(`[data-semester-anchor="${semester}"]`)
      ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const currentCode = toBackendSemesterCode(currentSemester);
  // Index of the marked term in the strip; -1 when the marker (or the
  // wall-clock fallback) doesn't correspond to a plan semester.
  const currentIndex = entries.findIndex(
    ([semester]) => toBackendSemesterCode(semester) === currentCode
  );

  let runningTotal = 0;

  return (
    <div className="mb-3 print-hide" data-testid="plan-timeline">
      <div className="flex items-center justify-between px-1 mb-1">
        <span className="text-xs font-semibold uppercase tracking-wide text-ink-3">Timeline</span>
        <button
          type="button"
          onClick={toggle}
          aria-expanded={visible}
          aria-label={visible ? 'Hide timeline' : 'Show timeline'}
          className="p-1 rounded hover:bg-surface-sunken text-ink-3 hover:text-ink transition-colors"
        >
          {visible ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
        </button>
      </div>

      {visible && (
        <div className="overflow-x-auto pb-1">
          <ol className="flex items-stretch gap-0 min-w-max" aria-label="Semester timeline">
            {entries.map(([semester, courses], i) => {
              const credits = calculateTotalCredits(courses);
              runningTotal += credits;
              const isCurrent = i === currentIndex;
              const isPast = currentIndex >= 0 && i < currentIndex;
              const displayName = getSemesterDisplayName(semester) ?? semester;
              const dot = getSeasonDotClass(semester, {
                isAPCredits: false,
                isOtherCredits: false,
              });

              return (
                <li key={semester} className="flex items-stretch">
                  {i > 0 && (
                    <div className="w-5 flex items-center" aria-hidden="true">
                      {/* Progress spine: filled through the current term so
                          the strip reads as one continuum, not tabs. */}
                      <div
                        className={`w-full rounded-full ${
                          currentIndex >= 0 && i <= currentIndex
                            ? 'h-0.5 bg-primary/50'
                            : 'h-px bg-line-soft'
                        }`}
                      />
                    </div>
                  )}
                  <div
                    className={`flex items-center rounded-card border transition-colors ${
                      isCurrent
                        ? 'border-primary/60 bg-primary/5 shadow-sm'
                        : isPast
                          ? 'border-line-soft bg-surface-sunken/50 hover:border-ink-3/40'
                          : 'border-line-soft bg-surface-card hover:border-ink-3/40'
                    }`}
                  >
                    <button
                      type="button"
                      onClick={() => scrollToSemester(semester)}
                      aria-current={isCurrent ? 'true' : undefined}
                      title={`${displayName} — ${creditUnit.format(credits)} (${runningTotal} cumulative)`}
                      className="flex flex-col items-start gap-0.5 pl-3 py-2 pr-1 text-left"
                    >
                      <span className="flex items-center gap-1.5">
                        <span className={`w-2 h-2 rounded-full flex-shrink-0 ${dot}`} aria-hidden="true" />
                        <span className="text-xs font-semibold text-ink whitespace-nowrap capitalize">
                          {displayName}
                        </span>
                        {isCurrent && (
                          <span className="text-[10px] font-medium uppercase tracking-wide text-primary-strong">
                            Now
                          </span>
                        )}
                      </span>
                      <span className="text-[11px] text-ink-2 tabular-nums whitespace-nowrap">
                        {credits} {creditUnit.abbrev}
                        <span className="text-ink-3" title={`Cumulative ${creditUnit.plural} through this term`}>
                          {' '}
                          · {runningTotal} total
                        </span>
                      </span>
                    </button>
                    {onCurrentTermChange && !isCurrent ? (
                      <button
                        type="button"
                        onClick={() =>
                          onCurrentTermChange(parseInt(toBackendSemesterCode(semester), 10))
                        }
                        aria-label={`Set ${displayName} as current term`}
                        title={`Set ${displayName} as current term`}
                        className="self-stretch flex items-center px-1.5 mr-0.5 rounded text-ink-3/60 hover:text-primary-strong hover:bg-surface-sunken transition-colors"
                      >
                        <MapPin className="w-3 h-3" aria-hidden />
                      </button>
                    ) : (
                      <span className="pr-2" aria-hidden />
                    )}
                  </div>
                </li>
              );
            })}
          </ol>
        </div>
      )}
    </div>
  );
};

export default PlanTimeline;
